import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import api from "../api/client";
import { OverviewStats, RegionStats, Result } from "../types";

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-10 h-10 border-[3px] border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const cls =
    score > 140
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20"
      : score >= 80
      ? "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20"
      : "bg-rose-50 text-rose-700 ring-1 ring-rose-600/20";
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${cls}`}>
      {score}
    </span>
  );
}

const YEARS = [2025, 2024, 2023, 2022, 2021];

const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function StatsPage() {
  const [year, setYear] = useState(2025);
  const [ballType, setBallType] = useState<"kontrakt" | "grand">("kontrakt");
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [topDirs, setTopDirs] = useState<Result[]>([]);
  const [regionStats, setRegionStats] = useState<RegionStats[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingTop, setLoadingTop] = useState(true);
  const [loadingRegions, setLoadingRegions] = useState(true);

  useEffect(() => {
    setLoadingOverview(true);
    api.get("/stats/overview")
      .then((r) => setOverview(r.data))
      .catch(() => {})
      .finally(() => setLoadingOverview(false));
  }, []);

  useEffect(() => {
    setLoadingTop(true);
    setLoadingRegions(true);

    api.get("/stats/top", { params: { year, ball_type: ballType } })
      .then((r) => setTopDirs(r.data || []))
      .catch(() => setTopDirs([]))
      .finally(() => setLoadingTop(false));

    api.get("/stats/regions", { params: { year } })
      .then((r) => setRegionStats(r.data || []))
      .catch(() => setRegionStats([]))
      .finally(() => setLoadingRegions(false));
  }, [year, ballType]);

  const chartDataKey = ballType === "kontrakt" ? "avg_kontrakt_ball" : "avg_grand_ball";
  const sortedRegions = [...regionStats].sort((a, b) =>
    ballType === "kontrakt"
      ? b.avg_kontrakt_ball - a.avg_kontrakt_ball
      : b.avg_grand_ball - a.avg_grand_ball
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Statistika</h1>
          <p className="text-slate-500 text-sm">O'tish ballari tahlili va solishtirish</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Ball type toggle */}
          <div className="flex bg-slate-100 rounded-xl p-1">
            {[
              { value: "kontrakt" as const, label: "Kontrakt" },
              { value: "grand" as const, label: "Grant" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setBallType(opt.value)}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
                  ballType === opt.value
                    ? "bg-white text-indigo-700 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Year selector */}
          <div className="flex bg-slate-100 rounded-xl p-1">
            {YEARS.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                className={`px-3 sm:px-4 py-2 text-xs font-semibold rounded-lg transition-all ${
                  year === y
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overview cards */}
      {loadingOverview ? (
        <Spinner />
      ) : overview ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {[
            {
              label: "Universitetlar",
              value: overview.total_universities,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
              ),
              color: "indigo",
            },
            {
              label: "Yo'nalishlar",
              value: overview.total_directions,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              ),
              color: "emerald",
            },
            {
              label: "Bot foydalanuvchilari",
              value: overview.total_bot_users,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              ),
              color: "amber",
            },
            {
              label: "Mavjud yillar",
              value: overview.years?.length || 0,
              icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              ),
              color: "rose",
            },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 hover:shadow-md transition-shadow">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                card.color === "indigo" ? "bg-indigo-50 text-indigo-600" :
                card.color === "emerald" ? "bg-emerald-50 text-emerald-600" :
                card.color === "amber" ? "bg-amber-50 text-amber-600" :
                "bg-rose-50 text-rose-600"
              }`}>
                {card.icon}
              </div>
              <p className="text-2xl sm:text-3xl font-bold text-slate-900">{card.value.toLocaleString("uz-UZ")}</p>
              <p className="text-xs text-slate-500 mt-0.5">{card.label}</p>
            </div>
          ))}
        </div>
      ) : null}

      {/* Region chart */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-6 mb-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Hududlar bo'yicha o'rtacha ball</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {ballType === "kontrakt" ? "Kontrakt" : "Grant"} ball bo'yicha - {year}-yil
            </p>
          </div>
        </div>

        {loadingRegions ? (
          <Spinner />
        ) : sortedRegions.length > 0 ? (
          <div className="w-full overflow-x-auto -mx-2">
            <div style={{ minWidth: 600, height: Math.max(400, sortedRegions.length * 32) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={sortedRegions}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis
                    type="number"
                    domain={[0, "dataMax + 10"]}
                    tick={{ fontSize: 11, fill: "#94a3b8" }}
                    axisLine={{ stroke: "#e2e8f0" }}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="region_name"
                    width={160}
                    tick={{ fontSize: 12, fill: "#475569" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value: any) => [Number(value).toFixed(1), "O'rtacha ball"]}
                    contentStyle={{
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
                      fontSize: 13,
                    }}
                    cursor={{ fill: "#f8fafc" }}
                  />
                  <Bar dataKey={chartDataKey} radius={[0, 6, 6, 0]} barSize={20}>
                    {sortedRegions.map((_, index) => (
                      <Cell
                        key={index}
                        fill={
                          index < 3 ? "#4f46e5" :
                          index < 7 ? "#6366f1" :
                          index < 12 ? "#818cf8" : "#c7d2fe"
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-500">Ma'lumot topilmadi</p>
          </div>
        )}
      </div>

      {/* Top competitive directions */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">
            Eng raqobatli yo'nalishlar
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {ballType === "kontrakt" ? "Kontrakt" : "Grant"} ball bo'yicha top 20 - {year}-yil
          </p>
        </div>

        <div className="p-5 sm:p-6">
          {loadingTop ? (
            <Spinner />
          ) : topDirs.length > 0 ? (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="pb-3 pr-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-10">#</th>
                      <th className="pb-3 pr-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Yo'nalish</th>
                      <th className="pb-3 pr-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Universitet</th>
                      <th className="pb-3 pr-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Shakl</th>
                      <th className="pb-3 pr-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Til</th>
                      <th className="pb-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                        {ballType === "kontrakt" ? "Kontrakt" : "Grant"} ball
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {topDirs.map((d, i) => (
                      <tr key={`${d.id}-${i}`} className="hover:bg-slate-50/50 transition-colors">
                        <td className="py-3.5 pr-4">
                          {i < 3 ? (
                            <span
                              className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                              style={{ backgroundColor: MEDAL_COLORS[i] + "22", color: MEDAL_COLORS[i] }}
                            >
                              {i + 1}
                            </span>
                          ) : (
                            <span className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500">
                              {i + 1}
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 pr-4 font-medium text-slate-900">{d.direction_name}</td>
                        <td className="py-3.5 pr-4 text-slate-600 max-w-[200px] truncate">{d.institute_name}</td>
                        <td className="py-3.5 pr-4">
                          <span className="bg-indigo-50 text-indigo-700 rounded-lg px-2 py-0.5 text-xs font-medium">{d.etype}</span>
                        </td>
                        <td className="py-3.5 pr-4 text-slate-600 text-xs">{d.language}</td>
                        <td className="py-3.5 text-center">
                          <ScoreBadge score={ballType === "kontrakt" ? d.kontrakt_ball : d.grand_ball} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {topDirs.map((d, i) => (
                  <div key={`${d.id}-${i}`} className="border border-slate-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      {i < 3 ? (
                        <span
                          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                          style={{ backgroundColor: MEDAL_COLORS[i] + "22", color: MEDAL_COLORS[i] }}
                        >
                          {i + 1}
                        </span>
                      ) : (
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-500">
                          {i + 1}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900 text-sm leading-snug">{d.direction_name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{d.institute_name}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <ScoreBadge score={ballType === "kontrakt" ? d.kontrakt_ball : d.grand_ball} />
                          <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-lg text-xs font-medium">{d.etype}</span>
                          <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-lg text-xs font-medium">{d.language}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500">Ma'lumot topilmadi</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
